import React, { useState } from 'react';
import BaseWidget from './BaseWidget';

/**
 * Stackable Tiles Widget
 * Allows users to stack multiple metrics/tiles together in a single widget
 */
const StackableTilesWidget = ({ id, config = {}, onRemove, onConfigure, isEditing, theme = 'light' }) => {
  const [tiles, setTiles] = useState(() => config.tiles || [
    { id: 1, label: 'Revenue', value: '$24,500', change: '+12%', color: 'green' },
    { id: 2, label: 'Expenses', value: '$8,200', change: '-3%', color: 'red' },
    { id: 3, label: 'Profit', value: '$16,300', change: '+18%', color: 'blue' }
  ]);
  const [isStacked, setIsStacked] = useState(false);
  const [isAddingTile, setIsAddingTile] = useState(false);
  const [newTile, setNewTile] = useState({
    label: '',
    value: '',
    change: '',
    color: 'blue'
  });

  const addTile = () => {
    if (newTile.label && newTile.value) {
      const tile = {
        id: Date.now(),
        label: newTile.label,
        value: newTile.value,
        change: newTile.change || '',
        color: newTile.color
      };
      setTiles([...tiles, tile]);
      setNewTile({ label: '', value: '', change: '', color: 'blue' });
      setIsAddingTile(false);
    }
  };

  const removeTile = (tileId) => {
    setTiles(tiles.filter(t => t.id !== tileId));
  };

  const renderTile = (tile, index) => (
    <div
      key={tile.id}
      className={`p-4 rounded-lg border transition-all group ${
        isStacked
          ? `absolute shadow-lg`
          : 'relative shadow'
      } bg-[var(--surface-raised)] border-[var(--surface-border)]`}
      style={isStacked ? {
        left: `${index * 12}px`,
        top: `${index * 12}px`,
        zIndex: tiles.length - index,
        width: 'calc(100% - 24px)'
      } : {}}
    >
      {isEditing && (
        <button
          onClick={() => removeTile(tile.id)}
          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity text-[var(--ui-alert)] hover:opacity-80"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
      <div className="text-sm text-[var(--ink-muted)] font-medium">{tile.label}</div>
      <div className="text-2xl font-bold text-[var(--ink)] mt-1">{tile.value}</div>
      {tile.change && (
        <div className={`text-sm mt-1 font-medium ${
          tile.color === 'green' ? 'text-[var(--ui-positive)]' :
          tile.color === 'red' ? 'text-[var(--ui-alert)]' :
          tile.color === 'blue' ? 'text-[var(--nav-active-ink)]' :
          tile.color === 'orange' ? 'text-[var(--ui-moderate)]' :
          'text-[var(--ink-muted)]'
        }`}>
          {tile.change}
        </div>
      )}
    </div>
  );

  return (
    <BaseWidget
      id={id}
      title="Stackable Metrics"
      onRemove={onRemove}
      onConfigure={onConfigure}
      isRemovable={isEditing}
      isConfigurable={isEditing}
      theme={theme}
      headerActions={
        isEditing ? (
          <div className="widget-drag-handle px-2 py-1 cursor-move">
            <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
            </svg>
          </div>
        ) : null
      }
    >
      <div className="p-4">
        <div className="flex justify-between items-center mb-4">
          {isEditing && !isAddingTile && (
            <button
              onClick={() => setIsAddingTile(true)}
              className="px-3 py-1 text-xs font-medium rounded bg-[var(--nav-active-bg)] text-[var(--nav-active-ink)] hover:brightness-95 transition"
            >
              + Add Tile
            </button>
          )}
          <div className="flex-1"></div>
          <button
            onClick={() => setIsStacked(!isStacked)}
            className="px-3 py-1 text-xs font-medium rounded border border-[var(--surface-border)] bg-[var(--surface-sunken)] text-[var(--accent-ink)] hover:bg-[var(--surface-border)] transition"
          >
            {isStacked ? 'Unstack' : 'Stack'} Tiles
          </button>
        </div>

        {isAddingTile && (
          <div className="mb-4 p-3 bg-[var(--surface-sunken)] rounded-lg border border-[var(--surface-border)]">
            <div className="space-y-2">
              <input
                type="text"
                placeholder="Metric Label (e.g., Revenue)"
                value={newTile.label}
                onChange={(e) => setNewTile({ ...newTile, label: e.target.value })}
                className="w-full px-3 py-2 text-sm rounded border border-[var(--surface-border)] bg-[var(--surface-raised)] text-[var(--ink)] placeholder-[var(--ink-muted)]"
              />
              <input
                type="text"
                placeholder="Value (e.g., $24,500)"
                value={newTile.value}
                onChange={(e) => setNewTile({ ...newTile, value: e.target.value })}
                className="w-full px-3 py-2 text-sm rounded border border-[var(--surface-border)] bg-[var(--surface-raised)] text-[var(--ink)] placeholder-[var(--ink-muted)]"
              />
              <input
                type="text"
                placeholder="Change (optional, e.g., +12%)"
                value={newTile.change}
                onChange={(e) => setNewTile({ ...newTile, change: e.target.value })}
                className="w-full px-3 py-2 text-sm rounded border border-[var(--surface-border)] bg-[var(--surface-raised)] text-[var(--ink)] placeholder-[var(--ink-muted)]"
              />
              <div>
                <label className="block text-xs font-medium text-[var(--ink)] mb-1">Color</label>
                <div className="flex gap-2">
                  {['blue', 'green', 'red', 'orange', 'purple'].map(color => (
                    <button
                      key={color}
                      onClick={() => setNewTile({ ...newTile, color })}
                      className={`w-8 h-8 rounded border-2 transition ${
                        newTile.color === color
                         ? 'border-[var(--accent-ink)]'
                         : 'border-[var(--surface-border)]'
                      } ${
                        color === 'blue' ? 'bg-blue-500' :
                        color === 'green' ? 'bg-green-500' :
                        color === 'red' ? 'bg-red-500' :
                        color === 'orange' ? 'bg-[var(--viz-orange)]' :
                        'bg-purple-500'
                      }`}
                    />
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={addTile}
                  className="flex-1 px-3 py-2 text-sm font-medium rounded bg-[var(--btn-fill)] text-[var(--btn-ink)] hover:bg-[var(--btn-fill-hover)] transition"
                >
                  Add
                </button>
                <button
                  onClick={() => {
                    setIsAddingTile(false);
                    setNewTile({ label: '', value: '', change: '', color: 'blue' });
                  }}
                  className="flex-1 px-3 py-2 text-sm font-medium text-[var(--ink)] bg-[var(--surface-border)] rounded hover:bg-[var(--surface-sunken)] transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        <div className={`${isStacked ? 'relative' : 'grid grid-cols-1 md:grid-cols-3 gap-4'}`} style={isStacked ? { height: `${(tiles.length * 12) + 140}px` } : {}}>
          {tiles.map((tile, index) => renderTile(tile, index))}
        </div>

        {!isAddingTile && tiles.length === 0 && (
          <div className="text-center py-8 text-[var(--ink-muted)] text-sm">
            {isEditing ? 'Click "Add Tile" to add metrics to this widget' : 'No tiles added yet'}
          </div>
        )}

        {!isAddingTile && tiles.length > 0 && (
          <div className="mt-4 text-xs text-[var(--ink-muted)] text-center">
            {isEditing ? 'Click "Stack Tiles" to overlap metrics for compact view. Hover over tiles to remove them.' : 'Click "Stack Tiles" to overlap metrics for compact view'}
          </div>
        )}
      </div>
    </BaseWidget>
  );
};

export default StackableTilesWidget;
